import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";

function UpdateTradeLicense() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tradeTypes, setTradeTypes] = useState([]);
  const [formData, setFormData] = useState({
    tradeName: "",
    ownerName: "",
    address: "",
    holdingNo: "",
    phoneNo: "",
    tradeTypeId: "",
    approxArea: "",
    registrationNumber: "",
  });
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const typesResponse = await axios.get(
          `${API_URL}list-all-trade-types.php`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (
          typesResponse.data.status &&
          Array.isArray(typesResponse.data.data)
        ) {
          setTradeTypes(typesResponse.data.data);
        } else {
          toast.error("Could not fetch trade types.");
        }

        const licenseResponse = await axios.get(
          `${API_URL}get-trade-licence.php?id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (licenseResponse.data.success && licenseResponse.data.data) {
          const data = licenseResponse.data.data;
          setFormData({
            tradeName: data.trade_name || "",
            ownerName: data.trade_owner || "",
            address: data.address || "",
            holdingNo: data.holding_no || "",
            phoneNo: data.phone || "",
            tradeTypeId: data.trade_type_id || "",
            approxArea: data.approx_area || "",
            registrationNumber: data.reg_no || "",
          });
        } else {
          toast.error(
            licenseResponse.data.message || "Could not fetch license details."
          );
        }
      } catch (error) {
        toast.error("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, API_URL, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const apiPayload = {
      trade_license_id: id,
      trade_name: formData.tradeName,
      trade_owner: formData.ownerName,
      address: formData.address,
      phone: formData.phoneNo,
      trade_type_id: formData.tradeTypeId,
      approx_area: formData.approxArea,
    };

    if (formData.holdingNo) {
      apiPayload.holding_no = formData.holdingNo;
    }
    if (formData.registrationNumber) {
      apiPayload.reg_no = formData.registrationNumber;
    }

    try {
      const response = await axios.post(
        `${API_URL}update-trade-licence.php`,
        apiPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Trade License updated successfully!"
        );
        navigate("/departmentDashboard/trade-license-list");
      } else {
        toast.error(response.data.message || "Failed to update trade license.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the license.");
    }
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <h2 className="mb-4">Update Trade License</h2>
          <Card className="shadow-sm">
            <Card.Header as="h5">Edit License Details</Card.Header>
            <Card.Body>
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "200px" }}
                >
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="6">
                      <Form.Label>Trade Name</Form.Label>
                      <Form.Control
                        name="tradeName"
                        value={formData.tradeName}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="6">
                      <Form.Label>Owner Name</Form.Label>
                      <Form.Control
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="6">
                      <Form.Label>Holding No (Optional)</Form.Label>
                      <Form.Control
                        name="holdingNo"
                        value={formData.holdingNo}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="6">
                      <Form.Label>Phone No</Form.Label>
                      <Form.Control
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Row>
                  <Row className="mb-3">
                    <Form.Group as={Col} md="4">
                      <Form.Label>Trade Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="tradeTypeId"
                        value={formData.tradeTypeId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select...</option>
                        {tradeTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.trade_type_name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>Approx Area (sq. ft.)</Form.Label>
                      <Form.Control
                        type="number"
                        name="approxArea"
                        value={formData.approxArea}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>Registration Number (Optional)</Form.Label>
                      <Form.Control
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Row>
                  <Button variant="primary" type="submit" className="mt-2">
                    Update License
                  </Button>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default UpdateTradeLicense;
